import {
  DEFECT_ACTION_STATUSES,
  DEFECT_STAGES,
  type DefectStage,
  type ReauthKind,
} from '@felix/domain';
import {
  Alert,
  Button,
  Modal,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import { chainText, closureFlow, closureNotes } from './closure-route.js';
import classes from './Defects.module.css';
import { defectPriorityTone, defectStageTone } from './routes.js';
import type { DefectActionInput, DefectStockOption, DefectSummary } from './types.js';

/** Hata kodunun metni (`defects.errors.*`); bilinmeyen kod genel metinle. */
export function defectErrorText(t: TFunction, error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  const key = typeof code === 'string' ? code : 'GENERIC';
  return t(`defects.errors.${key}`, {
    defaultValue: t('defects.errors.GENERIC', { code: key }),
  });
}

/** Aşama rozeti; Under Action'da açık talep varsa "Awaiting Parts" (ADR 0096 §8; A6). */
export function DefectStageBadge({
  stage,
  awaitingParts = false,
}: {
  stage: DefectStage;
  awaitingParts?: boolean | undefined;
}) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={defectStageTone(stage)}>
      {awaitingParts && stage === 'under_action'
        ? t('defects.awaitingParts')
        : t(`defects.stages.${stage}`)}
    </StatusBadge>
  );
}

export function DefectPriorityBadge({ priority }: { priority: string | null }) {
  const { t } = useTranslation();
  if (!priority) return <span className={classes.muted}>—</span>;
  return (
    <StatusBadge tone={defectPriorityTone(priority)}>
      {t(`defects.priorities.${priority}`)}
    </StatusBadge>
  );
}

const ACTION_TONE: Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  open: 'danger',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

export function DefectActionBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={ACTION_TONE[status] ?? 'neutral'}>
      {t(`defects.actionStatuses.${status}`)}
    </StatusBadge>
  );
}

/**
 * LGS-17'nin dört ana aşaması (ADR 0096 §2): Reported → Under Action → Verification → Closed;
 * tamamlanan aşamada tik, bilinen tarih altında. Kapanmış arızada hepsi tamam.
 */
export function DefectStepper({
  stage,
  dates = {},
  tzOffsetMin,
}: {
  stage: DefectStage;
  /** Aşamanın bilinen anı (ISO) ya da günü (`YYYY-MM-DD`). */
  dates?: Partial<Record<DefectStage, string | null>>;
  tzOffsetMin?: number | undefined;
}) {
  const { t } = useTranslation();
  const current = DEFECT_STAGES.indexOf(stage);
  const when = (value: string | null | undefined) =>
    !value ? '—' : value.length === 10 ? formatDay(value) : formatDateTime(value, tzOffsetMin);
  return (
    <ol className={classes.stepper} aria-label={t('defects.stepper.label')}>
      {DEFECT_STAGES.map((step, index) => {
        const state =
          index < current || stage === 'closed' ? 'done' : index === current ? 'current' : 'next';
        return (
          <li
            key={step}
            className={classes.step}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className={classes.stepNo}>
              {state === 'done' ? <IconCheck size={14} aria-hidden /> : index + 1}
            </span>
            <span className={classes.stepText}>
              <span className={classes.stepName}>{t(`defects.stages.${step}`)}</span>
              <span className={classes.stepDate}>{state === 'next' ? '—' : when(dates[step])}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Arızanın akışı ve kapanış onay yolu (ADR 0091): gemide Open → … → Verification, kapanış onayı
 * (zincir: Master gemide → Superintendent ofiste) ve Closed. Altında kimin onayı beklendiği, kararın
 * nerede verildiği ve kendi kaydını onaylayamayan onaylayıcının adımının neden ofise gittiği.
 */
export function DefectClosureRoute({
  detail,
  scope,
  decidable,
  showFlow = true,
}: {
  detail: Pick<DefectSummary, 'status' | 'closure'> & { ownRecord?: boolean };
  scope: 'ship' | 'office';
  /** Bakan kişi bekleyen adıma bu ekranda karar verebilir mi. */
  decidable: boolean;
  /** Şerit; kendi akış şeridi olan ekranda (PMS arıza çekmecesi) yalnız notlar. */
  showFlow?: boolean;
}) {
  const { t } = useTranslation();
  const where = (office: boolean) => t(office ? 'defects.route.office' : 'defects.route.onBoard');
  const chain = chainText(detail.closure.route ?? [], where);
  const label = (key: string) =>
    key === 'closure'
      ? chain
        ? t('defects.route.closureChain', { chain })
        : t('defects.route.closure')
      : key === 'closed'
        ? t('defects.stages.closed')
        : t(`defects.statuses.${key}`);
  const notes = closureNotes(detail, { scope, decidable }, where);
  if (!showFlow && notes.length === 0) return null;
  return (
    <section className={classes.route} aria-label={t('defects.route.title')}>
      <ol className={classes.routeFlow} aria-label={t('defects.route.flow')} hidden={!showFlow}>
        {closureFlow(detail).map((item) => (
          <li
            key={item.key}
            className={classes.routeStep}
            data-state={item.state}
            data-kind={item.key === 'closure' ? 'closure' : undefined}
            aria-current={item.state === 'current' ? 'step' : undefined}
          >
            <span className={classes.routeLabel}>{label(item.key)}</span>
          </li>
        ))}
      </ol>
      {notes.map((note) => (
        <p key={note.key} className={classes.routeNote}>
          {t(`defects.route.notes.${note.key}`, note.params)}
        </p>
      ))}
    </section>
  );
}

/** Liste ya da kayıt yüklenemedi: yeniden dene. */
export function DefectLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      tone="danger"
      title={t('defects.list.error')}
      description={defectErrorText(t, error)}
      action={
        <Button size="xs" variant="light" onClick={onRetry}>
          {t('defects.list.retry')}
        </Button>
      }
    />
  );
}

/** Hata bandı. */
export function DefectError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
      {message}
    </Alert>
  );
}

/**
 * İmza penceresi (ADR 0060 §8): kapanışa gönderim (mürettebat) ya da karar (kaptan, yönetim) açık
 * onay adımıdır; iadede gerekçe. Parola alanı yalnız şirket ayarı açıkken ya da sunucu isterse
 * görünür (ADR 0075).
 */
export function SignDialog({
  opened,
  title,
  body,
  reasonRequired,
  reauthKind,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  title: string;
  body: string;
  reasonRequired?: boolean;
  /** Kaydın onay türü: `defect_report` ya da `breakdown_report`. */
  reauthKind: ReauthKind;
  onClose: () => void;
  /** `password` yalnız girildiyse gelir. */
  onConfirm: (input: { password?: string; reason: string | null }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`defects.dialogs.${key}`);
  const reauth = useReauth(reauthKind);
  const resetPassword = reauth.reset;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (opened) {
      resetPassword();
      setReason('');
      setError(null);
    }
  }, [opened, resetPassword]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (reasonRequired && reason.trim() === '') {
      setError(t('defects.errors.VALIDATION_FAILED'));
      return;
    }
    if (reauth.missing) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ ...reauth.body(), reason: reason.trim() || null });
      onClose();
    } catch (e) {
      reauth.noteError(e);
      setError(defectErrorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="sm">
          <p className={classes.dialogBody}>{body}</p>
          {reasonRequired ? (
            <Textarea
              label={k('reason')}
              required
              autosize
              minRows={2}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
            />
          ) : null}
          <ReauthField reauth={reauth} label={k('password')} mb="0" />
          <DefectError message={error} />
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={reauth.missing}>
              {k('confirm')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}

const emptyAction = (): DefectActionInput => ({
  description: '',
  responsible: null,
  targetOn: null,
  status: 'open',
  completedOn: null,
  evidence: null,
  workOrderId: null,
  cancelReason: null,
});

/** Faaliyet satırı penceresi (ADR 0060 §5): kanıtsız tamamlanmaz; iptal gerekçe ister. */
export function ActionDialog({
  opened,
  action,
  onClose,
  onSave,
}: {
  opened: boolean;
  action: DefectActionInput | null;
  onClose: () => void;
  onSave: (action: DefectActionInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`defects.actions.${key}`);
  const [value, setValue] = useState<DefectActionInput>(action ?? emptyAction());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (opened) {
      setValue(action ?? emptyAction());
      setError(null);
    }
  }, [opened, action]);
  const change = (patch: Partial<DefectActionInput>) => setValue((v) => ({ ...v, ...patch }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({
        ...value,
        completedOn: value.status === 'completed' ? value.completedOn : null,
        cancelReason: value.status === 'cancelled' ? value.cancelReason : null,
      });
      onClose();
    } catch (e) {
      setError(defectErrorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened={opened} onClose={onClose} title={k('dialogTitle')} centered size="lg">
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="sm">
          <Textarea
            label={k('description')}
            required
            autosize
            minRows={2}
            maxLength={500}
            value={value.description}
            onChange={(event) => change({ description: event.currentTarget.value })}
          />
          <div className={classes.dialogGrid}>
            <TextInput
              label={k('responsible')}
              maxLength={200}
              value={value.responsible ?? ''}
              onChange={(event) => change({ responsible: event.currentTarget.value || null })}
            />
            <DayInput
              label={k('targetOn')}
              value={value.targetOn}
              onChange={(targetOn) => change({ targetOn })}
              clearable
            />
            <Select
              label={k('status')}
              data={DEFECT_ACTION_STATUSES.map((s) => ({
                value: s,
                label: t(`defects.actionStatuses.${s}`),
              }))}
              value={value.status}
              allowDeselect={false}
              onChange={(status) =>
                change({ status: (status ?? 'open') as DefectActionInput['status'] })
              }
            />
            {value.status === 'completed' ? (
              <DayInput
                label={k('completedOn')}
                required
                value={value.completedOn}
                onChange={(completedOn) => change({ completedOn })}
              />
            ) : null}
          </div>
          {value.status === 'cancelled' ? (
            <Textarea
              label={k('cancelReason')}
              required
              autosize
              minRows={2}
              maxLength={500}
              value={value.cancelReason ?? ''}
              onChange={(event) => change({ cancelReason: event.currentTarget.value || null })}
            />
          ) : (
            <TextInput
              label={k('evidence')}
              description={k('evidenceHint')}
              maxLength={500}
              value={value.evidence ?? ''}
              onChange={(event) => change({ evidence: event.currentTarget.value || null })}
            />
          )}
          <DefectError message={error} />
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={busy}>
              {k('save')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Not penceresi: gemide takip notu, ofiste yönetim önlemi (Part C, isteğe bağlı gün) ya da yorum.
 * Notlar değiştirilemez (ADR 0060 §6).
 */
export function NoteDialog({
  opened,
  title,
  label,
  hint,
  withDay,
  saveLabel,
  onClose,
  onSave,
}: {
  opened: boolean;
  title: string;
  label: string;
  hint: string;
  withDay?: boolean;
  saveLabel?: string;
  onClose: () => void;
  onSave: (input: { body: string; actionOn: string | null }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const [actionOn, setActionOn] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (opened) {
      setBody('');
      setActionOn(null);
      setError(null);
    }
  }, [opened]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({ body: body.trim(), actionOn });
      onClose();
    } catch (e) {
      setError(defectErrorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="sm">
          <Textarea
            label={label}
            description={hint}
            required
            autosize
            minRows={3}
            maxLength={2000}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
          {withDay ? (
            <DayInput
              label={t('defects.office.actionOn')}
              value={actionOn}
              onChange={setActionOn}
              clearable
            />
          ) : null}
          <DefectError message={error} />
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {t('defects.dialogs.cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={body.trim() === ''}>
              {saveLabel ?? t('defects.office.save')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Stoktan malzeme kullanımı (ADR 0096 §8; A5): eldeki miktarı olan kalem, miktar ve not. Stok
 * yetmezse sunucu reddeder (`NEGATIVE_STOCK`); eksik malzeme talep edilir.
 */
export function StockDialog({
  opened,
  options,
  onClose,
  onSave,
}: {
  opened: boolean;
  options: DefectStockOption[] | null;
  onClose: () => void;
  onSave: (input: { stockItemId: string; quantity: number; note: string | null }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const m = (key: string, params: Record<string, unknown> = {}) =>
    t(`defects.lgs.material.${key}`, params);
  const [item, setItem] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (opened) {
      setItem(null);
      setQuantity('');
      setNote('');
      setError(null);
    }
  }, [opened]);
  const amount = typeof quantity === 'number' ? quantity : Number.parseFloat(quantity);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!item || !(amount > 0)) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({ stockItemId: item, quantity: amount, note: note.trim() || null });
      onClose();
    } catch (e) {
      setError(defectErrorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal opened={opened} onClose={onClose} title={m('issueTitle')} centered>
      <form onSubmit={(event) => void submit(event)}>
        <Stack gap="sm">
          {options !== null && options.length === 0 ? (
            <p className={classes.dialogBody}>{m('noStock')}</p>
          ) : (
            <>
              <Select
                label={m('item')}
                required
                searchable
                // Kumanya listelenmez (gemi sunucusu süzer); ad ya da kodla aranır (ADR 0122 §4)
                data={(options ?? []).map((o) => ({
                  value: o.id,
                  label: m('onHand', {
                    name: o.code ? `${o.name} — ${o.code}` : o.name,
                    n: o.onHand,
                    unit: o.unit,
                  }),
                }))}
                value={item}
                onChange={setItem}
              />
              <NumberInput
                label={m('quantity')}
                required
                min={0}
                decimalScale={3}
                value={quantity}
                onChange={setQuantity}
              />
              <TextInput
                label={m('note')}
                maxLength={300}
                value={note}
                onChange={(event) => setNote(event.currentTarget.value)}
              />
              <p className={classes.muted}>{m('hint')}</p>
            </>
          )}
          <DefectError message={error} />
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {m('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={!item || !(amount > 0)}>
              {m('confirm')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}
